import {TProductLineDraft} from "@/api/tiktokVisibilityApi";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import StarOutlineIcon from "@/assets/colorable/star_rounded_outline.svg?react";
import TrashIcon from "@/assets/colorable/trash.svg?react";
import UndoIcon from "@/assets/colorable/undo.svg?react";
import StarFilledIcon from "@/assets/dark_gradient/star_rounded.svg?react";
import clsxm from "@/lib/clsxm";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {Tooltip} from "react-tooltip";
import {type Updater} from "use-immer";

const ProductLineSidebar: FC<{
  activeProductLine: string;
  isReadOnly?: boolean;
  items: {
    additionCount: number;
    isPendingDeletion: boolean;
    isPrimary: boolean;
    name: string;
    removalCount: number;
  }[];
  scrollToProductLine: (name: string) => void;
  setProductLineDrafts: Updater<TProductLineDraft[]>;
}> = ({activeProductLine, isReadOnly = false, items, scrollToProductLine, setProductLineDrafts}) => {
  const {t} = useTranslation(["search_visibility", "common"]);
  const [isCreatingNewProductLine, setIsCreatingNewProductLine] = useState(false);
  const [newProductLineName, setNewProductLineName] = useState("");

  const createProductLine = (name: string) => {
    setProductLineDrafts(draft => {
      const hasProductLine = draft.some(line => !line.isPendingDeletion);

      draft.push({
        competitors: [],
        id: undefined,
        isPendingDeletion: false,
        isPrimary: !hasProductLine,
        keywords: [],
        name,
      });
    });

    setTimeout(() => scrollToProductLine(name), 100);
  };

  const deleteProductLine = (name: string) =>
    setProductLineDrafts(draft => {
      const idx = draft.findIndex(l => l.name === name);
      if (idx < 0) return;
      if (draft[idx].id) {
        draft[idx].isPendingDeletion = true;
      } else {
        draft.splice(idx, 1);
      }
    });

  const undoDeleteProductLine = (name: string) =>
    setProductLineDrafts(draft => {
      const line = draft.find(l => l.name === name);
      if (line) line.isPendingDeletion = false;
    });

  const submitNewProductLine = () => {
    const trimmed = newProductLineName.trim();
    const isDuplicate = !!trimmed && items.some(item => item.name.toLowerCase() === trimmed.toLowerCase());

    if (!trimmed || isDuplicate) {
      setIsCreatingNewProductLine(false);
      setNewProductLineName("");
      return;
    }

    createProductLine(trimmed);
    setIsCreatingNewProductLine(false);
    setNewProductLineName("");
  };

  return (
    <div className="border-neutral-white-dark hidden w-50 shrink-0 flex-col overflow-y-auto border-r py-4 md:flex">
      {isReadOnly ? (
        <>
          <div
            data-tooltip-id="pool-only-add-product-line"
            className="border-neutral-white-dark text-neutral-grey-light mx-3 mb-3 flex min-h-8 cursor-not-allowed items-center gap-2 rounded-lg border border-dashed px-3 py-1 text-sm font-medium">
            <PlusIcon className="size-3 shrink-0" />
            {t("new_product_line")}
          </div>
          <Tooltip
            id="pool-only-add-product-line"
            place="right"
            delayShow={300}
            className="bg-primary-powder text-primary-dark-solid border-primary-dark-solid max-w-52 rounded-md border text-xs"
            classNameArrow="border border-primary-dark-solid bg-primary-powder border-t-0 border-l-0">
            {t("add_product_line_pool_only_hint")}
          </Tooltip>
        </>
      ) : isCreatingNewProductLine ? (
        <input
          autoFocus
          value={newProductLineName}
          onChange={e => setNewProductLineName(e.target.value)}
          onBlur={() => {
            if (!newProductLineName.trim()) setIsCreatingNewProductLine(false);
          }}
          onKeyDown={e => {
            if (e.key === "Enter") submitNewProductLine();
            if (e.key === "Escape") {
              e.stopPropagation();
              setIsCreatingNewProductLine(false);
              setNewProductLineName("");
            }
          }}
          placeholder={t("product_line_name_placeholder")}
          className="border-primary-sky text-neutral-black mx-3 mb-3 min-h-8 rounded-lg border bg-white px-3 py-1 text-sm focus:outline-none"
        />
      ) : (
        <button
          onClick={() => setIsCreatingNewProductLine(true)}
          className="border-primary-sky bg-primary-powder text-primary-dark-solid hover:bg-primary-light-solid mx-3 mb-3 flex min-h-8 items-center gap-2 rounded-lg border border-dashed px-3 py-1 text-sm font-medium transition-colors">
          <PlusIcon className="size-3 shrink-0" />
          {t("new_product_line")}
        </button>
      )}
      <span className="text-neutral-grey-dark text-2xs mb-2 px-5 font-bold tracking-wider uppercase">
        {t("product_lines")}
      </span>
      {items.map(item => (
        <button
          key={item.name}
          onClick={() => scrollToProductLine(item.name)}
          className={clsxm(
            "group relative flex w-full items-center justify-between gap-2 border-r-2 px-5 py-2.5 text-left text-sm font-medium transition-colors",
            activeProductLine === item.name
              ? "text-primary-dark-solid bg-primary-powder border-primary-dark-solid"
              : "text-neutral-black hover:bg-neutral-white-dark border-transparent",
          )}>
          <span className="flex min-w-0 items-center gap-1.5">
            {!item.isPendingDeletion && (
              <div
                onClick={e => {
                  e.stopPropagation();

                  setProductLineDrafts(draft => {
                    for (const line of draft) line.isPrimary = line.name === item.name;
                  });
                }}
                className={clsxm(
                  "shrink-0 cursor-pointer transition-opacity",
                  item.isPrimary
                    ? "opacity-100"
                    : "text-neutral-grey-light hover:text-primary-dark-solid opacity-0 group-hover:opacity-100",
                )}>
                {item.isPrimary ? (
                  <StarFilledIcon className="size-3.5" />
                ) : isReadOnly ? (
                  <div className="size-3.5" />
                ) : (
                  <StarOutlineIcon className="size-3.5" />
                )}
              </div>
            )}
            <span className={clsxm("truncate", item.isPendingDeletion && "text-neutral-grey-dark line-through")}>
              {item.name}
            </span>
          </span>
          {item.isPendingDeletion ? (
            <div
              onClick={e => {
                e.stopPropagation();
                undoDeleteProductLine(item.name);
              }}
              className="text-primary-dark-solid hover:text-primary-dark-solid/80 flex cursor-pointer items-center gap-1 text-xs font-medium transition-colors">
              <UndoIcon className="size-3 shrink-0" />
              {t("common:undo")}
            </div>
          ) : (
            <>
              <span
                className={clsxm(
                  "flex shrink-0 items-center gap-1.5",
                  !isReadOnly && !item.isPrimary && "group-hover:hidden",
                )}>
                {item.removalCount > 0 && (
                  <span className="bg-danger-medium-red text-2xs rounded-full px-1.5 py-0.5 font-bold text-white">
                    -{item.removalCount}
                  </span>
                )}
                {item.additionCount > 0 && (
                  <span className="bg-primary-dark-solid text-2xs rounded-full px-1.5 py-0.5 font-bold text-white">
                    +{item.additionCount}
                  </span>
                )}
              </span>
              {!isReadOnly && !item.isPrimary && (
                <div
                  onClick={e => {
                    e.stopPropagation();
                    deleteProductLine(item.name);
                  }}
                  className="text-neutral-grey-dark hover:text-danger-medium-red hidden cursor-pointer transition-colors group-hover:flex">
                  <TrashIcon className="size-3 shrink-0" />
                </div>
              )}
            </>
          )}
        </button>
      ))}
    </div>
  );
};

export default ProductLineSidebar;
